import type { FC } from 'react';
import React, { useEffect, useState, useRef } from 'react';
import type { FieldRenderProps } from 'react-final-form';
import { Field as FinalField } from 'react-final-form';
import { validateNotEmpty } from '@alfa-bank/site-helpers-form/validation/not-empty';
import { setCursorPosition } from '@alfa-bank/site-helpers-form/meta/set-cursor-position';
import type { TChangeEventHandler } from '@alfa-bank/site-input/models';

import { fetchSuggestionsFullName } from '../helpers/actions';
import type { IPropsOneFieldController, IOldNamesInterface } from '../models';
import { Field } from '../field';
import type { TOnFioSelect } from '../field/models';
import { defaultHandler } from '../field/config';

export const OneFieldController: FC<IPropsOneFieldController> = ({
    value: fio = '',
    className,
    forwardedRef,
    fetchFirstName = defaultHandler,
    fetchLastName = defaultHandler,
    fetchPatronymic = defaultHandler,
    onFocus,
    onChange,
    onBlur,
    options = [],
    onSelect,
    validate,
    ...rest
}) => {
    const [fullNameValue, setFullNameValue] = useState<string>(fio);
    const [oldLastName, oldFirstName = '', oldPatronymic = ''] = fio.split(' ');
    const [stateOldNames, setStateOldNames] = useState<IOldNamesInterface>({
        oldLastName,
        oldFirstName,
        oldPatronymic,
    });
    const refFallback = useRef<HTMLInputElement>(null);
    const ref = forwardedRef || refFallback;

    // Если при первом рендере fio было подставлено снаружи то нужно явно запросить подсказки
    // т.к. fullNameValue.split(' ') и [oldLastName, oldFirstName, oldPatronymic] будут одинаковые
    const refIsFirstFetchSuggestions = useRef(true);

    const onChangeHandler: TChangeEventHandler = ({ event, name, value = '' }) => {
        const fullName = String(value);

        fetchSuggestionsFullName({
            fetchFirstName,
            fetchLastName,
            fetchPatronymic,
            isFirstFetchSuggestions: refIsFirstFetchSuggestions.current,
            stateOldNames,
            setStateOldNames,
            fullName,
        });

        if (refIsFirstFetchSuggestions.current) {
            refIsFirstFetchSuggestions.current = false;
        }

        if (onChange) {
            onChange({ event, name, value });
        }

        // скорректировать положение каретки, после форматирование введенного значения
        setCursorPosition(ref);

        setFullNameValue(fullName);
    };

    const onSelectHandler: TOnFioSelect = ({ field, id, label, name, value }) => {
        if (onSelect) {
            onSelect({ id, label, name, value, field });
        }
        const isFioFilled = value.trim().split(' ').length >= 3;

        /** нужно возвращать фокус в поле при выборе опции */
        if (ref.current) {
            ref.current.focus();
        }

        /** если поле достаточно заполнено, то нужно запустить блюр, чтобы отработали эвенты */
        /* istanbul ignore if */
        if (isFioFilled) {
            setTimeout(() => {
                ref.current?.blur();
            }, 0);
        }
        setFullNameValue(value);
    };

    useEffect(() => {
        /** если есть значение по умолчанию, и при первом рендере установилась пустая строка, устанавливаем новое значение */
        if (fio !== fullNameValue && fullNameValue === '') setFullNameValue(fio);
        // fullNameValue удален из зависимостей, чтобы исправить баг с полным удалением текста из поля
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fio]);

    return (
        <div className={className}>
            <FinalField
                name="fullName"
                render={field => (
                    <Field
                        field={field as FieldRenderProps<string>}
                        forwardedRef={ref}
                        onBlur={onBlur}
                        onChange={onChangeHandler}
                        onFocus={onFocus}
                        onSelect={onSelectHandler}
                        options={options}
                        value={fullNameValue}
                        {...rest}
                    />
                )}
                validate={validate ? validate : validateNotEmpty()}
            />
        </div>
    );
};
